import mapboxgl from 'mapbox-gl';
import CustomHead from '../components/CustomHead';
import React, { useEffect, useRef, useState } from 'react';
import MapFooter from '../components/MapFooter';
import { useSearch } from '../contexts/SearchContext';
import ForSaleQuickFilter from '../components/searchFilters/ForSaleQuickFilter';
import SoldQuickFilter from '../components/searchFilters/SoldQuickFilter';
import { useMap } from '../contexts/MapContext';
import ViewedLocations from '@th/shared/src/types/ViewedLocations';
import Map, { PreviewLocation } from '../components/Map';
import { MAPBOX_PUBLIC_ACCESS_TOKEN } from '@th/frontend-shared/src/constants/mapbox';
import fetchAgentObject from '../helpers/queries/fetchAgentObject';
import { Agent, AgentObject } from '@th/shared/src/types/Agent';
import MapNavBar from '../components/navBar/MapNavBar';
import NlpSearchStatusModal from '../components/NlpSearchStatusModal';
import useFetchListingsByIds from '@th/frontend-shared/src/hooks/queries/useFetchListingsByIds';
import { MapLocation } from '@th/shared/src/types/MapLocation';
import SuggestedFiltersRow from '../components/map/SuggestedFiltersRow';
import PreviewListingsSidebar from '../components/map/PreviewListingsSidebar';
import SvgIcon from '../components/icons/SvgIcon';
import useRemoveAndPerformSearchFromSpecialQueryParams from '../hooks/useRemoveAndPerformSearchFromSpecialQueryParams';

mapboxgl.accessToken = MAPBOX_PUBLIC_ACCESS_TOKEN;

type Props = {
  agentObject: AgentObject;
  initialViewState: {
    longitude?: number;
    latitude?: number;
    zoom?: number;
  };
  initialPinCoordinates?: {
    longitude: number;
    latitude: number;
  };
  initialGeneralQuery?: string;
  initialNlpQuery?: string;
};

export async function getServerSideProps(context) {
  const agentSlug = context.query?.agentSlug;
  const initialGeneralQuery = context.query?.q || null;
  const initialNlpQuery = context.query?.nlp || null;

  const lat = context.query?.lat || null;
  const lng = context.query?.lng || null;
  const zoom = context.query?.zoom || null;
  const pinLat = context.query?.pinLat || null;
  const pinLng = context.query?.pinLng || null;

  let agentObject = null;
  if (agentSlug) {
    agentObject = await fetchAgentObject(agentSlug);
  }

  return {
    props: {
      agentObject,
      initialViewState: { latitude: lat, longitude: lng, zoom },
      initialPinCoordinates: { latitude: pinLat, longitude: pinLng },
      initialGeneralQuery,
      initialNlpQuery,
    },
  };
}

export default function MapPage({
  agentObject,
  initialViewState,
  initialPinCoordinates,
  initialGeneralQuery,
  initialNlpQuery,
}: Props) {
  const { setPinCoordinates, areaOverlay, clearAreaOverlay } = useMap();
  const {
    searchFilters,
    openSearchFilterModal,
    numberOfFiltersSet,
    nlpSearchStatusModalRef,
    isFiltersSetToDefault,
    clearFilters,
  } = useSearch();
  useRemoveAndPerformSearchFromSpecialQueryParams(
    initialGeneralQuery,
    initialNlpQuery,
  );
  const [currentlyFocusedLocation, setCurrentlyFocusedLocation] =
    useState<MapLocation | null>(null);
  const viewedLocations = useRef<ViewedLocations>({});
  const agent = agentObject && Agent.fromObject(agentObject);

  const [previewLocations, setPreviewLocations] = useState<
    Array<PreviewLocation>
  >([]);
  const [hoveredPreviewLocation, setHoveredPreviewLocation] =
    useState<PreviewLocation>(null);

  useEffect(() => {
    if (initialPinCoordinates.latitude && initialPinCoordinates.longitude) {
      setPinCoordinates(initialPinCoordinates);
    }
  }, [
    initialPinCoordinates.latitude,
    initialPinCoordinates.longitude,
    setPinCoordinates,
  ]);

  function onLocationClick(location: MapLocation) {
    if (location) {
      setCurrentlyFocusedLocation(location);
      viewedLocations.current[location.locationId] = true;
    } else {
      setCurrentlyFocusedLocation(null);
    }
  }

  return (
    <div>
      <CustomHead />
      {/*
        We use 'dvh' (dynamic view height) below to handle screen height changes due to mobile browser headers/footers.
        All modern browsers support dvh units.
        100dvh equals 100% height of the visible area and dynamically changes when a mobile browser header or footer visibility is switched.
      */}
      <div className="h-[100dvh] flex flex-col">
        <MapNavBar />
        <div className="grow relative flex flex-row overflow-hidden">
          <div className="hidden lg:flex flex-col lg:w-[320px] xl:w-[640px] ">
            <SuggestedFiltersRow />
            <PreviewListingsSidebar
              previewLocations={previewLocations}
              onPreviewCardHover={(previewLocation: PreviewLocation) => {
                setHoveredPreviewLocation(previewLocation);
              }}
            />
          </div>
          <div className="flex-1">
            <Map
              onLocationClick={onLocationClick}
              currentlyFocusedLocation={currentlyFocusedLocation}
              initialViewState={initialViewState}
              previouslyViewedLocations={viewedLocations.current}
              searchFilters={searchFilters}
              updatePreviewLocations={setPreviewLocations}
              hoveredPreviewLocation={hoveredPreviewLocation}
            />
            <div className="absolute top-4 right-0 flex flex-row ">
              <div className="flex flex-row flex-1 justify-end">
                <div className="mr-4 space-x-4 flex flex-row">
                  <button
                    className="relative btn flex items-center bg-white px-4 hover:bg-lightGray disabled:bg-white border border-mediumLightGray"
                    onClick={() => {
                      clearFilters();
                      clearAreaOverlay();
                    }}
                    disabled={isFiltersSetToDefault && !Boolean(areaOverlay)}
                  >
                    <SvgIcon
                      name="MsAltOff"
                      className="text-gray-500 -ml-1 -mr-1 h-5 w-5"
                    />
                  </button>
                  <button
                    className="relative btn flex items-center bg-white px-4 hover:bg-lightGray border border-mediumLightGray"
                    onClick={openSearchFilterModal}
                  >
                    <SvgIcon
                      name="BxSlider"
                      className="text-gray-500 -ml-1 mr-2 h-5 w-5"
                    />
                    <p className="mb-0 mt-0 text-text font-bold">Filters</p>
                    {numberOfFiltersSet > 0 && (
                      <div className="absolute flex justify-center items-center h-6 w-6 rounded-full -top-2 -right-3 bg-red-500">
                        <p className="mb-0 mt-0">{numberOfFiltersSet}</p>
                      </div>
                    )}
                  </button>
                  <ForSaleQuickFilter />
                  <SoldQuickFilter />
                </div>
              </div>
            </div>
          </div>
        </div>
        <NlpSearchStatusModal ref={nlpSearchStatusModalRef} />
        <MapFooter agent={agent} />
      </div>
    </div>
  );
}
